import { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserContext } from '../../context/UserContext';
import usePageProtection from '../../hooks/usePageProtection';
import styles from './ContentRenderer.module.css';
import ContentSkeleton from './ContentSkeleton';

import PdfRenderer from './renderer/PdfRenderer';
import TextRenderer from './renderer/TextRenderer';
import MarkdownRenderer from './renderer/MarkdownRenderer';
import HtmlRenderer from './renderer/HtmlRenderer';
import DownloadRenderer from './renderer/DownloadRenderer';
import UnsupportedRenderer from './renderer/UnsupportedRenderer';
import XlsxRenderer from './renderer/XlsxRenderer';
import VideoRenderer from './renderer/VideoRenderer';

const getRenderer = (fileType) => {
  if (!fileType) return UnsupportedRenderer;

  if (fileType === 'application/pdf') return PdfRenderer;

  if (fileType === 'text/html') return HtmlRenderer;

  if (fileType === 'text/markdown') return MarkdownRenderer;

  if (
    // /^text\//i.test(fileType) ||
    [
      'text/plain',
      'text/x-python',
      'application/json',
      'application/xml',
      'application/javascript',
    ].includes(fileType)
  ) {
    return TextRenderer;
  }

  if (
    [
      'application/zip',
      'application/x-zip-compressed',
      'application/gzip',
      'application/x-7z-compressed',
      'application/x-rar-compressed',
      'application/x-tar',
    ].includes(fileType)
  ) {
    return DownloadRenderer;
  }

  if (
    [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].includes(fileType)
  ) {
    //  for pptx and docx have to convert this to pdf and then render as pdf
    return XlsxRenderer;
  }
  if (
    [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/quicktime',
      'video/x-matroska',
      'video/avi',
      'video/mpeg',
    ].includes(fileType)
  ) {
    return VideoRenderer;
  }

  return UnsupportedRenderer;
};

const pageTransition = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
};

const ContentRenderer = () => {
  const { userDetails } = useContext(UserContext);

  // Enable protections against right click, dev tools, and printing
  usePageProtection({
    disableContextMenu: true,
    disableDevTools: true,
    disablePrint: true,
    disableCopy: true,
    disableSave: true,
    disableSelect: true
  });

  const [contentDetails, setContentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const { contentId } = useParams();

  useEffect(() => {
    const getContentDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/content/get/${contentId}?e=${userDetails?.email}`,
        );
        const responseData = await response.json();

        if (response.ok) {
          setContentDetails(responseData);
          setViewerUrl(responseData.content_url);
          document.title = `${responseData?.title}` || 'SETU Roadmaps';
        } else {
          setError(responseData?.message || 'Failed to load content details.');
        }
      } catch (err) {
        console.error(err);
        setError('An error occurred while fetching content details.');
      } finally {
        setLoading(false);
      }
    };

    if (contentId) {
      getContentDetails();
    }
  }, [contentId, userDetails?.email]);

  // const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000/api/v1';
  // const viewerUrl = `${serverUrl}/content/${contentId}/viewer?e=${userDetails?.email || ''}`;

  return (
    <div className={styles.main}>
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ width: '100%' }}
          >
            <ContentSkeleton />
          </motion.div>
        ) : error || !contentDetails ? (
          <motion.div
            key="error"
            {...pageTransition}
            className={styles.errorContainer}
          >
            {error || 'Content not found.'}
          </motion.div>
        ) : (
          <motion.div
            key="content"
            {...pageTransition}
            style={{ width: '100%', height: '100%' }}
          >
            {(() => {
              const RendererComponent = getRenderer(contentDetails.file_type);
              return (
                <RendererComponent
                  contentDetails={contentDetails}
                  viewerUrl={viewerUrl}
                />
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentRenderer;